import { NetworkStatus, useQuery } from "@apollo/client";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import {
  Button,
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  Popover,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { gql } from "generated-graphql";
import { useState, useMemo } from "react";
import { useDebounce } from "use-debounce";

const GET_ALL_WORKFLOW_TAGS = gql(`
  query GetAllWorkflowTags {
    getAllWorkflowTags {
      id
      name
    }
  }
`);

const MY_WORKFLOW_TAGS_QUERY = gql(`
  query MyWorkflowTags($search: String!, $sort: [SortModel!], $page: Int!, $pageSize: Int!) {
    tags(search: $search, sort: $sort, page: $page, pageSize: $pageSize) {
      count
      items {
        id
        name
      }
    }
  }
`);

interface WorkflowTagsButtonProps {
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  disabled?: boolean;
  isUntaggedOnly?: boolean;
  onUntaggedChange?: (untagged: boolean) => void;
}

const TAG_PAGE_SIZE = 10;

export function WorkflowTagsButton({
  selectedTags = [],
  onTagsChange,
  disabled = false,
  isUntaggedOnly = false,
  onUntaggedChange,
}: WorkflowTagsButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleTagsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  // Determine button text
  const getButtonText = () => {
    if (isUntaggedOnly) {
      return "Untagged";
    }
    if (selectedTags.length > 0) {
      return `Tags (${selectedTags.length})`;
    }
    return "Tags";
  };

  const hasActiveFilter = selectedTags.length > 0 || isUntaggedOnly;

  return (
    <>
      <Button
        size="small"
        variant={hasActiveFilter ? "contained" : "outlined"}
        disabled={disabled}
        sx={{
          borderRadius: 5,
          textTransform: "none",
          justifyContent: "space-between",
        }}
        endIcon={<ArrowDropDownIcon />}
        onClick={handleTagsClick}
      >
        {getButtonText()}
      </Button>
      <WorkflowTagsPopover
        anchorEl={anchorEl}
        onClose={handleClose}
        open={open}
        selectedTags={selectedTags}
        onTagsChange={onTagsChange}
        isUntaggedOnly={isUntaggedOnly}
        onUntaggedChange={onUntaggedChange}
      />
    </>
  );
}

interface WorkflowTagsPopoverProps {
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  open: boolean;
  selectedTags: string[];
  onTagsChange: (tags: string[]) => void;
  isUntaggedOnly?: boolean;
  onUntaggedChange?: (untagged: boolean) => void;
}

function WorkflowTagsPopover({
  anchorEl,
  onClose,
  open,
  selectedTags,
  onTagsChange,
  isUntaggedOnly = false,
  onUntaggedChange,
}: WorkflowTagsPopoverProps) {
  const [search, setSearch] = useState("");
  const [searchQuery] = useDebounce(
    `type:Workflow${search ? ` name:${search}` : ""}`,
    300
  );
  const [currentPage, setCurrentPage] = useState(0);

  const {
    data: tagData,
    fetchMore,
    networkStatus,
    previousData,
  } = useQuery(MY_WORKFLOW_TAGS_QUERY, {
    variables: {
      search: searchQuery,
      sort: [{ field: "name", sort: "asc" }],
      page: 0,
      pageSize: TAG_PAGE_SIZE,
    },
    skip: !searchQuery,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "cache-first",
  });

  const data = tagData ?? previousData;
  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

  const handleLoadMore = () => {
    fetchMore({
      variables: {
        page: currentPage + 1,
        pageSize: TAG_PAGE_SIZE,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          tags: {
            ...fetchMoreResult.tags,
            items: [...(prev?.tags.items ?? []), ...fetchMoreResult.tags.items],
          },
        };
      },
    }).then(() => {
      setCurrentPage(currentPage + 1);
    });
  };

  const hasMoreTags =
    (data?.tags.count ?? 0) > (currentPage + 1) * TAG_PAGE_SIZE;

  const handleTagToggle = (tagName: string) => {
    console.log("🎯 handleTagToggle - tagName:", tagName);
    console.log("🎯 handleTagToggle - current selectedTags:", selectedTags);
    console.log(
      "🎯 handleTagToggle - is tag currently selected?",
      selectedTags.includes(tagName)
    );

    const newTags = selectedTags.includes(tagName)
      ? selectedTags.filter((name) => name !== tagName)
      : [...selectedTags, tagName];

    console.log("🎯 handleTagToggle - new tags array:", newTags);
    onTagsChange(newTags);
  };

  const handleDeselectAll = () => {
    onTagsChange([]);
    onClose();
  };

  const handleUntaggedToggle = (checked: boolean) => {
    if (onUntaggedChange) {
      onUntaggedChange(checked);
      if (checked) {
        // Clear selected tags when enabling untagged filter
        onTagsChange([]);
      }
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Stack sx={{ width: 300, p: 1.5 }} spacing={1}>
        {/* Untagged only checkbox at the top */}
        {onUntaggedChange && (
          <>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isUntaggedOnly}
                  onChange={(e) => handleUntaggedToggle(e.target.checked)}
                  size="small"
                />
              }
              label="Untagged only"
              sx={{ ml: 0 }}
            />
            <Divider />
          </>
        )}

        {/* Tag search and list - disabled when untagged is checked */}
        <Box
          sx={{
            opacity: isUntaggedOnly ? 0.5 : 1,
            pointerEvents: isUntaggedOnly ? "none" : "auto",
          }}
        >
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            label="Search tags"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={isUntaggedOnly}
          />
        </Box>
        <Stack
          spacing={1}
          sx={{
            maxHeight: 300,
            overflow: "auto",
            opacity: isUntaggedOnly ? 0.5 : 1,
            pointerEvents: isUntaggedOnly ? "none" : "auto",
          }}
        >
          <Box
            onClick={handleDeselectAll}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: isUntaggedOnly ? "default" : "pointer",
              "&:hover": { bgcolor: isUntaggedOnly ? "transparent" : "action.hover" },
            }}
          >
            <CloseIcon sx={{ mr: 1 }} />
            <Typography>Deselect All</Typography>
          </Box>
          {data?.tags.items.length === 0 ? (
            <Typography sx={{ color: "text.secondary" }}>
              No tags found
            </Typography>
          ) : (
            data?.tags.items.map((tag) => (
              <Box
                key={tag.id}
                onClick={() => !isUntaggedOnly && handleTagToggle(tag.name)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: isUntaggedOnly ? "default" : "pointer",
                  "&:hover": { bgcolor: isUntaggedOnly ? "transparent" : "action.hover" },
                }}
              >
                <Checkbox
                  checked={selectedTags.includes(tag.name)}
                  disabled={isUntaggedOnly}
                  sx={{ p: 0, mr: 1 }}
                />
                <Typography>{tag.name}</Typography>
              </Box>
            ))
          )}
          {hasMoreTags && (
            <Button
              onClick={handleLoadMore}
              disabled={isLoadingMore || isUntaggedOnly}
              variant="outlined"
              size="small"
              fullWidth
            >
              Load More
            </Button>
          )}
        </Stack>
      </Stack>
    </Popover>
  );
}
